import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

function formatDuration(seconds) {
  const s = parseFloat(seconds);
  if (s < 60) return `${s}s`;
  const min = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return sec > 0 ? `${min} Min ${sec}s` : `${min} Min`;
}

export default function MessageBubble({ message }) {
  const isUser = message.role === "user";
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [openSourceIdx, setOpenSourceIdx] = useState(null);

  return (
    <div className={`message ${isUser ? "message-user" : "message-assistant"}`}>
      <div className="message-avatar">{isUser ? "👤" : "✦"}</div>
      <div className="message-content">
        <div className="message-role">{isUser ? "You" : "AI"}</div>
        <div className="message-text">
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <ReactMarkdown
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || "");
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={oneDark}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  ) : (
                    <code className="inline-code" {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {message.content || "▊"}
            </ReactMarkdown>
          )}
        </div>
        {!isUser && message.sources && message.sources.length > 0 && (
          <div style={{
            marginTop: 10,
            border: '1px solid #1f2229', borderRadius: 8, background: 'transparent',
            overflow: 'hidden'
          }}>
            <button
              type="button"
              onClick={() => setSourcesOpen(o => !o)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 12px', background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 500, color: '#6b7280', textAlign: 'left',
                opacity: 0.85
              }}
              title={sourcesOpen ? 'Quellen einklappen' : 'Quellen ausklappen'}
            >
              <span>📚 Quellen ({message.sources.length})</span>
              <span style={{
                transition: 'transform 0.2s', transform: sourcesOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                fontSize: 9, color: '#4b5563'
              }}>▶</span>
            </button>
            {sourcesOpen && (
              <ul style={{
                listStyle: 'none', padding: '4px 6px 8px', margin: 0,
                fontSize: 11, color: '#6b7280',
                borderTop: '1px solid #1f2229'
              }}>
                {message.sources.map((s, i) => {
                  const open = openSourceIdx === i;
                  const hasSnippet = !!s.snippet;
                  const isWeb = s.matchType === 'web';
                  return (
                    <li key={i} style={{ marginBottom: 2 }}>
                      <button
                        type="button"
                        onClick={() => setOpenSourceIdx(open ? null : i)}
                        style={{
                          width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 6px', background: open ? '#15171c' : 'transparent',
                          border: 'none', borderRadius: 4,
                          color: '#6b7280', fontSize: 11, cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = '#101218'; }}
                        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
                        title={open ? 'Auszug einklappen' : 'Auszug anzeigen'}
                      >
                        <span style={{
                          display: 'inline-block', width: 8, color: '#3f4651', fontSize: 9,
                          transition: 'transform 0.15s',
                          transform: open ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}>▶</span>
                        <span style={{ color: '#4b5563', fontWeight: 500 }}>[{i + 1}]</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isWeb && <span style={{ marginRight: 4 }}>🌐</span>}
                          {s.documentName}
                        </span>
                        {isWeb && (
                          <span style={{
                            fontSize: 9, color: '#5aa9f5', padding: '1px 5px',
                            border: '1px solid #1f3a52', borderRadius: 3
                          }}>
                            Web
                          </span>
                        )}
                        {s.matchType === 'keyword' && (
                          <span style={{
                            fontSize: 9, color: '#4b5563', padding: '1px 5px',
                            border: '1px solid #2a2e35', borderRadius: 3
                          }}>
                            wörtlich
                          </span>
                        )}
                      </button>
                      {open && (
                        <div style={{
                          margin: '4px 6px 8px', padding: '8px 10px',
                          background: '#0d0d14', border: '1px solid #1f2229', borderRadius: 4,
                          fontSize: 11, color: '#9ca3af', lineHeight: 1.5,
                          whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                          maxHeight: 200, overflowY: 'auto'
                        }}>
                          {hasSnippet ? (
                            <>
                              {s.snippet}
                              {s.snippet.length >= 500 && (
                                <span style={{ color: '#4b5563' }}> …</span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: '#5f6573', fontStyle: 'italic' }}>
                              Kein Text-Auszug verfügbar. Diese Antwort ist aus einer älteren
                              Unterhaltung — bitte die Frage in einem neuen Chat (oder mit einer
                              neuen Nachricht) stellen, dann werden Auszüge gespeichert.
                            </span>
                          )}
                          {isWeb && s.url ? (
                            <div style={{ marginTop: 6, fontSize: 9 }}>
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#5aa9f5', textDecoration: 'none', wordBreak: 'break-all' }}
                              >
                                🔗 {s.url}
                              </a>
                            </div>
                          ) : (
                            <div style={{ marginTop: 6, fontSize: 9, color: '#3f4651' }}>
                              Chunk #{s.chunkIndex}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {!isUser && message.duration && (
          <div className="message-duration">
            Antwort in {formatDuration(message.duration)}
            {message.stopped ? " — gestoppt" : ""}
          </div>
        )}
      </div>
    </div>
  );
}
